import React, { useState, useEffect, useMemo } from 'react';
import { videoTagsService, VideoTag } from '../../../shared/services/videoTagsService';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';
import { getStatIcon, getStatColorClasses } from '../../../shared/utils/statIconUtils';
import { 
  buildThumbnailMap, 
  getClosestThumbnail, 
  ThumbnailAsset,
  generateStorageThumbnailUrl 
} from '../../../shared/utils/thumbnailUtils';
import MultiSelectDropdown from '../../../shared/components/ui/MultiSelectDropdown';
import VideoThumbnail from '../../../shared/components/ui/VideoThumbnail';
import ClipPlayer from '../../../shared/components/video/ClipPlayer';
import { extractYouTubeVideoId, detectVideoSource } from '../../../shared/components/video/utils';

// Key event stat types (matching KeyEventsSyncService)
const KEY_EVENT_STAT_TYPES = [
  'goal_scored', 'shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored',
  'penalty_missed', 'penalty_shootout_scored', 'penalty_shootout_missed', 'own_goal',
  'yellow_card', 'red_card', 'substitution', 'kick_off', 'end_1st_half', 'start_2nd_half',
  'full_time', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half',
  'penalty_shootout', 'save_open_play', 'save_penalty', 'penalty_punch', 'penalty_claim'
];

interface EventClipsTabProps {
  matchId: string;
  isActive?: boolean;
  onEventClick?: (videoTimeSeconds: number, videoId: string) => void;
}

/**
 * Event Clips Tab Component
 * 
 * Displays a timeline of all video-tagged events for the match.
 * Users can click on events to jump to the corresponding video moment.
 */
const EventClipsTab: React.FC<EventClipsTabProps> = ({ 
  matchId, 
  isActive = true,
  onEventClick 
}) => {
  const [videoTags, setVideoTags] = useState<VideoTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsFetched, setTagsFetched] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatTypeIds, setSelectedStatTypeIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [eventFilter, setEventFilter] = useState<'key' | 'all'>('all');
  
  // Track which clip is currently playing inline (for storage videos)
  const [playingClipId, setPlayingClipId] = useState<string | null>(null);
  // Track if the playing clip is expanded
  const [isClipExpanded, setIsClipExpanded] = useState(false);
  
  // Thumbnail data for storage videos
  const [thumbnailMap, setThumbnailMap] = useState<Map<string, ThumbnailAsset[]>>(new Map());
  
  // Timeline range settings (independent from VideoTimelineTab)
  const [timelineRangeBefore, setTimelineRangeBefore] = useState<number>(5);
  const [timelineRangeAfter, setTimelineRangeAfter] = useState<number>(5);

  // Fetch video tags when tab becomes active (only on first load)
  useEffect(() => {
    if (isActive && matchId && !tagsFetched) {
      const fetchVideoTags = async () => {
        try {
          setTagsLoading(true);
          const result = await videoTagsService.getVideoTags(matchId);
          if (result.success && result.data) {
            setVideoTags(result.data);
          } else {
            setVideoTags([]);
          }
        } catch (err) {
          console.error('Error fetching video tags:', err);
          setVideoTags([]);
        } finally {
          setTagsLoading(false);
          setTagsFetched(true);
        }
      };

      fetchVideoTags();
    }
  }, [isActive, matchId, tagsFetched]);

  // Reset fetch flag when matchId changes
  useEffect(() => {
    setTagsFetched(false);
    setVideoTags([]);
    setThumbnailMap(new Map());
  }, [matchId]);

  // Fetch thumbnails for storage videos after tags are loaded
  useEffect(() => {
    const fetchThumbnails = async () => {
      if (videoTags.length === 0) return;
      
      // Get unique video IDs that are storage videos
      const storageVideoIds = new Set<string>();
      videoTags.forEach(tag => {
        if (tag.video?.video_url && tag.video_id) {
          const source = detectVideoSource(tag.video.video_url);
          console.log('🎬 Video source detection:', { 
            videoId: tag.video_id, 
            url: tag.video.video_url, 
            source 
          });
          if (source === 'cloudflare_r2' || source === 'supabase_storage') {
            storageVideoIds.add(tag.video_id);
          }
        }
      });
      
      console.log('🖼️ Storage video IDs for thumbnail fetch:', Array.from(storageVideoIds));
      
      if (storageVideoIds.size === 0) return;
      
      try {
        const result = await videoProcessingJobsService.getThumbnailJobsByVideoIds(
          Array.from(storageVideoIds)
        );
        console.log('🖼️ Thumbnail jobs result:', result);
        if (result.success && result.data) {
          // Filter out jobs with null video_id (e.g., stitch_clips jobs)
          const thumbnailJobs = result.data.filter((job) => job.video_id !== null) as Array<{
            video_id: string;
            payload: string | Record<string, any>;
          }>;
          const map = buildThumbnailMap(thumbnailJobs);
          console.log('🖼️ Thumbnail map built:', map.size, 'entries');
          setThumbnailMap(map);
        }
      } catch (error) {
        console.warn('Error fetching thumbnails:', error);
      }
    };
    
    fetchThumbnails();
  }, [videoTags]);

  // Extract unique stat types and tags from videoTags for filter options
  const statTypeOptions = useMemo(() => {
    const statTypeMap = new Map<string, { id: string; name: string }>();
    videoTags.forEach(tag => {
      if (tag.stat_type?.id && tag.stat_type?.display_name) {
        if (!statTypeMap.has(tag.stat_type.id)) {
          statTypeMap.set(tag.stat_type.id, {
            id: tag.stat_type.id,
            name: tag.stat_type.display_name
          });
        }
      }
    });
    return Array.from(statTypeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [videoTags]);

  const tagOptions = useMemo(() => {
    const tagSet = new Set<string>();
    videoTags.forEach(tag => {
      if (tag.tags && Array.isArray(tag.tags)) {
        tag.tags.forEach(t => tagSet.add(t));
      }
    });
    return Array.from(tagSet)
      .sort()
      .map(tag => ({
        id: tag,
        name: tag.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      }));
  }, [videoTags]);

  const teamOptions = useMemo(() => {
    const teamMap = new Map<string, { id: string; name: string }>();
    videoTags.forEach(tag => {
      if (tag.team?.id && tag.team?.name) {
        if (!teamMap.has(tag.team.id)) {
          teamMap.set(tag.team.id, {
            id: tag.team.id,
            name: tag.team.name
          });
        }
      }
    });
    return Array.from(teamMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [videoTags]);

  // Filter videoTags based on search and selected filters
  const filteredVideoTags = useMemo(() => {
    let filtered = [...videoTags];

    // Key events filter
    if (eventFilter === 'key') {
      filtered = filtered.filter(tag => {
        const statKey = tag.stat_type?.stat_key || '';
        return KEY_EVENT_STAT_TYPES.includes(statKey);
      });
    }

    // Search filter - search in stat type name, team name, player name, and tags
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tag => {
        const statTypeName = tag.stat_type?.display_name?.toLowerCase() || '';
        const teamName = tag.team?.name?.toLowerCase() || '';
        const playerName = tag.player?.full_name?.toLowerCase() || '';
        const tagStrings = (tag.tags || []).join(' ').toLowerCase();
        
        return statTypeName.includes(query) ||
               teamName.includes(query) ||
               playerName.includes(query) ||
               tagStrings.includes(query);
      });
    }

    // Stat type filter
    if (selectedStatTypeIds.length > 0) {
      filtered = filtered.filter(tag => 
        tag.stat_type?.id && selectedStatTypeIds.includes(tag.stat_type.id)
      );
    }

    // Tags filter - tag must have at least one of the selected tags
    if (selectedTagIds.length > 0) {
      filtered = filtered.filter(tag => {
        if (!tag.tags || tag.tags.length === 0) return false;
        return tag.tags.some(tagLabel => selectedTagIds.includes(tagLabel));
      });
    }

    // Team filter
    if (selectedTeamIds.length > 0) {
      filtered = filtered.filter(tag => 
        tag.team?.id && selectedTeamIds.includes(tag.team.id)
      );
    }

    return filtered;
  }, [videoTags, searchQuery, selectedStatTypeIds, selectedTagIds, selectedTeamIds, eventFilter]);

  /**
   * Generate thumbnail URL for a video tag
   * For storage videos: Uses pre-generated thumbnails from video processing jobs
   * For YouTube: Uses YouTube's thumbnail API
   */
  const getVideoThumbnailUrl = (tag: VideoTag): string | null => {
    if (!tag.video?.video_url) return null;
    
    const source = detectVideoSource(tag.video.video_url);
    
    // For storage videos, use pre-generated thumbnails or generate URL directly
    if (source === 'cloudflare_r2' || source === 'supabase_storage') {
      // First try thumbnailMap (if we have job data)
      const assets = thumbnailMap.get(tag.video_id);
      if (assets && assets.length > 0) {
        const timeSeconds = tag.video_time_seconds || 0;
        return getClosestThumbnail(assets, timeSeconds);
      }
      // Fallback: generate thumbnail URL directly from video path
      const timeSeconds = tag.video_time_seconds || 0;
      return generateStorageThumbnailUrl(tag.video.video_url, timeSeconds);
    }
    
    // For YouTube, use YouTube's thumbnail API
    const videoId = extractYouTubeVideoId(tag.video.video_url);
    if (!videoId) return null;
    
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  /**
   * Calculate video segment duration for thumbnail display
   */
  const getVideoSegmentDuration = (tag: VideoTag): string => {
    if (tag.video_time_seconds === null) return '';
    const duration = timelineRangeBefore + timelineRangeAfter;
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
  };

  // Check if video is a storage video (not YouTube)
  const isStorageVideo = (tag: VideoTag): boolean => {
    if (!tag.video?.video_url) return false;
    const source = detectVideoSource(tag.video.video_url);
    return source === 'cloudflare_r2' || source === 'supabase_storage' || source === 'local';
  };

  // Get clip timing info for a tag
  const getClipTiming = (tag: VideoTag): { startTime: number; endTime: number } | null => {
    if (tag.video_time_seconds === null) return null;
    const startTime = Math.max(0, tag.video_time_seconds - timelineRangeBefore);
    const endTime = tag.video_time_seconds + timelineRangeAfter;
    return { startTime, endTime };
  };


  // Handle clip play for storage videos
  const handlePlayStorageClip = (tag: VideoTag, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (playingClipId === tag.id) {
      // Stop playing
      setPlayingClipId(null);
    } else {
      // Start playing this clip
      setPlayingClipId(tag.id);
    }
  };

  // Handle clip click - storage videos play inline, YouTube navigates to timeline
  const handleClipClick = (tag: VideoTag, e: React.MouseEvent) => {
    if (!tag.video_time_seconds || !tag.video_id) return;
    
    if (isStorageVideo(tag)) {
      handlePlayStorageClip(tag, e);
    } else if (onEventClick) {
      // YouTube - navigate to Video Timeline tab
      onEventClick(tag.video_time_seconds, tag.video_id);
    }
  };

  // Use shared utility functions for icons and colors

  // Get tag color
  const getTagColor = (tag: string): string => {
    const normalizedTag = tag.toLowerCase();
    
    // Goal-related tags - green pastel
    if (normalizedTag.includes('goal') || normalizedTag.includes('score')) {
      return 'bg-green-100 text-green-800 border border-green-200';
    }
    
    // Card-related tags - yellow/red pastel
    if (normalizedTag.includes('card')) {
      if (normalizedTag.includes('red')) {
        return 'bg-red-100 text-red-800 border border-red-200';
      }
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    }
    
    // Attack-related tags - blue pastel
    if (normalizedTag.includes('attack') || normalizedTag.includes('shot') || normalizedTag.includes('penalty')) {
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    }
    
    // Defense-related tags - purple pastel
    if (normalizedTag.includes('defense') || normalizedTag.includes('tackle') || normalizedTag.includes('save')) {
      return 'bg-purple-100 text-purple-800 border border-purple-200';
    }
    
    // Game flow tags - orange pastel
    if (normalizedTag.includes('game_flow') || normalizedTag.includes('half') || normalizedTag.includes('kick_off') || normalizedTag.includes('substitution')) {
      return 'bg-orange-100 text-orange-800 border border-orange-200';
    }
    
    // Success tags - teal pastel
    if (normalizedTag.includes('success')) {
      return 'bg-teal-100 text-teal-800 border border-teal-200';
    }
    
    // Failure tags - pink pastel
    if (normalizedTag.includes('failure') || normalizedTag.includes('missed')) {
      return 'bg-pink-100 text-pink-800 border border-pink-200';
    }
    
    // Neutral tags - gray pastel
    if (normalizedTag.includes('neutral')) {
      return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
    
    // Default - indigo pastel
    return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
  };

  // Show loading state in the same container where list will appear
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Event Clips</h3>
        {/* View Mode Toggle - Dummy for now */}
        <div className="flex items-center space-x-4">
          {/* Marker Range Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-700 whitespace-nowrap">Range:</span>
            <div className="flex items-center space-x-1.5">
              <input
                type="number"
                min="0"
                max="60"
                value={timelineRangeBefore}
                onChange={(e) => setTimelineRangeBefore(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-500">s before</span>
              <span className="text-xs text-gray-400">/</span>
              <input
                type="number"
                min="0"
                max="60"
                value={timelineRangeAfter}
                onChange={(e) => setTimelineRangeAfter(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-500">s after</span>
            </div>
          </div>

          <div className="h-6 w-px bg-gray-300"></div>

          {/* View Mode Toggle */}
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
            title="List View"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
            title="Grid View"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        {/* Search Bar & Event Filter Row */}
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search event clips..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          </div>
          
          {/* Event Filter Toggle */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setEventFilter('key')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                eventFilter === 'key'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              Key Events
            </button>
            <button
              onClick={() => setEventFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                eventFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              All Events
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Stat Types Multi-Select */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Stat Types
            </label>
            <MultiSelectDropdown
              selectedIds={selectedStatTypeIds}
              onChange={setSelectedStatTypeIds}
              options={statTypeOptions}
              placeholder="Select stat types..."
              disabled={statTypeOptions.length === 0 || tagsLoading}
            />
          </div>

          {/* Teams Multi-Select */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Teams
            </label>
            <MultiSelectDropdown
              selectedIds={selectedTeamIds}
              onChange={setSelectedTeamIds}
              options={teamOptions}
              placeholder="Select teams..."
              disabled={teamOptions.length === 0 || tagsLoading}
            />
          </div>

          {/* Tags Multi-Select */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Tags
            </label>
            <MultiSelectDropdown
              selectedIds={selectedTagIds}
              onChange={setSelectedTagIds}
              options={tagOptions}
              placeholder="Select tags..."
              disabled={tagOptions.length === 0 || tagsLoading}
            />
          </div>
        </div>

        {/* Active Filters Info */}
        {(selectedStatTypeIds.length > 0 || selectedTagIds.length > 0 || selectedTeamIds.length > 0 || searchQuery) && (
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>
              Showing {filteredVideoTags.length} of {videoTags.length} event clips
            </span>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedStatTypeIds([]);
                setSelectedTagIds([]);
                setSelectedTeamIds([]);
              }}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
      {tagsLoading && !tagsFetched ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600">Loading event clips...</span>
          </div>
        </div>
      ) : videoTags.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
          </div>
          <p className="text-gray-500">No video tags found for this match</p>
        </div>
      ) : filteredVideoTags.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-500">No event clips match your filters</p>
          {(selectedStatTypeIds.length > 0 || selectedTagIds.length > 0 || selectedTeamIds.length > 0 || searchQuery) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedStatTypeIds([]);
                setSelectedTagIds([]);
                setSelectedTeamIds([]);
              }}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideoTags.map((tag) => {
            const hasVideoTiming = tag.video_id !== null && tag.video_time_seconds !== null;
            const thumbnailUrl = getVideoThumbnailUrl(tag);
            const segmentDuration = getVideoSegmentDuration(tag);
            const isPlaying = playingClipId === tag.id;
            const isStorage = isStorageVideo(tag);

            return (
              <div
                key={tag.id}
                className={`bg-white border rounded-lg overflow-hidden hover:shadow-md transition-all duration-150 ${
                  hasVideoTiming ? 'cursor-pointer border-gray-200' : 'border-gray-200'
                } ${isPlaying ? 'ring-2 ring-blue-500' : ''} ${isPlaying && isClipExpanded ? 'col-span-full' : ''}`}
                onClick={(e) => {
                  if (hasVideoTiming) {
                    handleClipClick(tag, e);
                  }
                }}
              >
                {/* Thumbnail or Video Player */}
                <div className={`relative bg-gray-100 ${isPlaying && isClipExpanded ? 'aspect-video' : 'aspect-video'}`}>
                  {isPlaying && isStorage && tag.video?.video_url && getClipTiming(tag) ? (
                    // Inline clip player for storage videos with custom progress bar
                    <ClipPlayer
                      src={tag.video.video_url}
                      startTime={getClipTiming(tag)!.startTime}
                      endTime={getClipTiming(tag)!.endTime}
                      markerTime={tag.video_time_seconds || 0}
                      initialRangeBefore={timelineRangeBefore}
                      initialRangeAfter={timelineRangeAfter}
                      autoPlay
                      onEnded={() => { setPlayingClipId(null); setIsClipExpanded(false); }}
                      onClose={() => { setPlayingClipId(null); setIsClipExpanded(false); }}
                      className="w-full h-full"
                      isExpanded={isClipExpanded}
                      onToggleExpand={() => setIsClipExpanded(!isClipExpanded)}
                    />
                  ) : (
                    <>
                      <VideoThumbnail
                        src={thumbnailUrl || undefined}
                        alt={tag.stat_type?.display_name || 'Event clip'}
                        duration={segmentDuration}
                        className="w-full"
                        fallbackIcon="🎥"
                      />
                      {/* Play Overlay */}
                      {hasVideoTiming && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity duration-150">
                          <div className="w-12 h-12 bg-blue-600 bg-opacity-90 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Event Info - Compact Layout */}
                <div className="px-3 py-2">
                  {/* Line 1: Icon, Event Name, Time */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-5 h-5 rounded flex items-center justify-center text-xs border flex-shrink-0 ${(() => {
                      const colors = getStatColorClasses(tag.stat_type?.stat_key || null);
                      return `${colors.text} ${colors.bg} ${colors.border}`;
                    })()}`}>
                      {getStatIcon(tag.stat_type?.stat_key || null)}
                    </div>
                    <span className="text-sm font-medium text-gray-900 truncate flex-1">
                      {tag.stat_type?.display_name || 'Event'}
                    </span>
                    {tag.match_minute !== null && (
                      <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                        {tag.match_minute}'
                      </span>
                    )}
                  </div>

                  {/* Line 2: Team + Player + Tags (single line with dynamic overflow, or all tags when expanded) */}
                  <div className={`flex items-center mt-1.5 gap-2 ${isPlaying && isClipExpanded ? 'flex-wrap' : 'overflow-hidden'}`}>
                    {tag.team?.name && (
                      <span className="text-xs text-gray-600 truncate flex-shrink-0 max-w-[100px]">
                        {tag.team.name}
                      </span>
                    )}
                    {tag.player?.full_name && (
                      <span className="text-xs text-gray-500 truncate flex-shrink-0 max-w-[100px]">
                        {tag.player.full_name}
                      </span>
                    )}
                    {tag.tags && tag.tags.length > 0 && (
                      <div 
                        className="flex gap-1 flex-1 overflow-hidden"
                        title={tag.tags.join(', ')}
                      >
                        {tag.tags.map((tagLabel, idx) => (
                          <span 
                            key={idx} 
                            className={`text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${getTagColor(tagLabel)}`}
                          >
                            {tagLabel}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-1">
          {filteredVideoTags.map((tag) => {
            const hasVideoTiming = tag.video_id !== null && tag.video_time_seconds !== null;
            const isPlaying = playingClipId === tag.id;
            const isStorage = isStorageVideo(tag);

            return (
              <div
                key={tag.id}
                className={`bg-white border rounded-lg hover:bg-gray-50 transition-colors duration-150 ${
                  hasVideoTiming ? 'cursor-pointer border-gray-200' : 'border-gray-200'
                } ${isPlaying ? 'ring-2 ring-blue-500' : ''}`}
                onClick={(e) => {
                  if (hasVideoTiming) {
                    handleClipClick(tag, e);
                  }
                }}
              >
                <div className="px-4 py-2">
                  <div className="flex items-center justify-between">
                    {/* Left Side - Event Info (Compact) */}
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {/* Event Icon */}
                      <div className={`w-6 h-6 rounded flex items-center justify-center text-xs border flex-shrink-0 ${(() => {
                        const colors = getStatColorClasses(tag.stat_type?.stat_key || null);
                        return `${colors.text} ${colors.bg} ${colors.border}`;
                      })()}`}>
                        {getStatIcon(tag.stat_type?.stat_key || null)}
                      </div>
                      
                      {/* Event Details - Compact 2 lines */}
                      <div className="flex-1 min-w-0">
                        {/* Line 1: Event Name + Time */}
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {tag.stat_type?.display_name || 'Event'}
                          </span>
                          {tag.match_minute !== null && (
                            <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                              {tag.match_minute}'
                            </span>
                          )}
                        </div>
                        
                        {/* Line 2: Team + Player + Tags */}
                        <div className="flex items-center mt-1 gap-2 flex-wrap">
                          {tag.team?.name && (
                            <span className="text-xs text-gray-600">
                              {tag.team.name}
                            </span>
                          )}
                          {tag.player?.full_name && (
                            <span className="text-xs text-gray-500">
                              {tag.player.full_name}
                            </span>
                          )}
                          {tag.tags && tag.tags.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              {tag.tags.map((tagLabel, index) => (
                                <span
                                  key={index}
                                  className={`text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap ${getTagColor(tagLabel)}`}
                                >
                                  {tagLabel}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Side - Play/Stop or Jump Button */}
                    {hasVideoTiming && (
                      <div className="flex items-center space-x-2 ml-4">
                        {isStorage ? (
                          <button
                            onClick={(e) => handlePlayStorageClip(tag, e)}
                            className={`p-2 rounded-lg transition-colors ${
                              isPlaying 
                                ? 'text-red-600 hover:bg-red-50' 
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                            title={isPlaying ? 'Stop' : 'Play Clip'}
                          >
                            {isPlaying ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (tag.video_time_seconds !== null && tag.video_id && onEventClick) {
                                onEventClick(tag.video_time_seconds, tag.video_id);
                              }
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Jump to Video"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Inline Clip Player for List View (Storage Videos) */}
                  {isPlaying && isStorage && tag.video?.video_url && getClipTiming(tag) && (
                    <div className={`mt-3 rounded-lg overflow-hidden bg-black aspect-video ${isClipExpanded ? '' : 'max-h-64'}`}>
                      <ClipPlayer
                        src={tag.video.video_url}
                        startTime={getClipTiming(tag)!.startTime}
                        endTime={getClipTiming(tag)!.endTime}
                        markerTime={tag.video_time_seconds || 0}
                        initialRangeBefore={timelineRangeBefore}
                        initialRangeAfter={timelineRangeAfter}
                        autoPlay
                        onEnded={() => { setPlayingClipId(null); setIsClipExpanded(false); }}
                        onClose={() => { setPlayingClipId(null); setIsClipExpanded(false); }}
                        className="w-full h-full"
                        isExpanded={isClipExpanded}
                        onToggleExpand={() => setIsClipExpanded(!isClipExpanded)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

};

export default EventClipsTab;

